import { useTenderDeadline } from "../../../../../../../hooks/useTenderDeadline";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import style from "./ExpeditorActionsTab.module.scss";
import { Aviation } from "../../../../../../../services";
import ExpeditorBidForm from "./ExpeditorBidForm";
import ExpeditorBidBlock from "./ExpeditorBidBlock";
import ExpeditorTenderClosedInfo from "./ExpeditorTenderClosedInfo";
import ExpeditorStatusAction from "./ExpeditorStatusAction";
import ExpeditorExpectedArrivalBlock from "./ExpeditorExpectedArrivalBlock";
import ExpeditorDocumentUpload from "./ExpeditorDocumentUpload";
import ExpeditorReturnAction from "./ExpeditorReturnAction";
import ExpeditorFinalStatusInfo from "./ExpeditorFinalStatusInfo";
import CanceledOrderCard from "../../../../../../orders-new/order-info-expeditor/components/CanceledOrderCard/CanceledOrderCard";

const ACTION_STATUSES = new Set(["confirmed", "accepted", "onthetrip"]);

const ExpeditorActionsTab = ({
  order,
  fetchOrderData,
  onForceUpdate,
  refreshDocuments,
  onClose,
  history = [],
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(order?.tenderDateEnd);
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const requestIdRef = useRef(0);
  const [previousBids, setPreviousBids] = useState([]);
  const [bidsLoaded, setBidsLoaded] = useState(false);

  const status = String(order?.status || "").toLowerCase();
  const isCanceled = status === "canceled";
  const canceledStatus = [...(Array.isArray(history) ? history : [])]
    .reverse()
    .find((item) => String(item?.statusCode || "").toLowerCase() === "canceled");
  const canceledText = `${t("orderInfoExpeditor.history.statusCanceledByPartner")}. ${t(
    "orderInfoExpeditor.history.priceChangedReason",
  )} ${canceledStatus?.comment?.trim() || "РЅРµ СѓРєР°Р·Р°РЅР°"}`;
  const isTenderEnded =
    Boolean(order?.tenderDateEnd) && isTenderExpiredByServer;
  const isOtherExpeditorAssigned =
    order?.expeditorId !== undefined &&
    order?.expeditorId !== null &&
    order?.expeditorId !== "" &&
    String(order.expeditorId) !== String(user?.companyId);
  const isTenderResultForOtherExpeditor =
    status !== "awaitingoffer" && isOtherExpeditorAssigned;
  const shouldLoadTenderBids =
    isCanceled || status === "awaitingoffer" || isTenderResultForOtherExpeditor;

  const loadPreviousBids = useCallback(async () => {
    if (!order?.id) return;

    const requestId = ++requestIdRef.current;
    try {
      const response = await Aviation.getSuggestedPriceByExpeditor(order.id);
      if (requestId !== requestIdRef.current) return;
      setPreviousBids(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error(error);
      setPreviousBids([]);
    } finally {
      if (requestId === requestIdRef.current) setBidsLoaded(true);
    }
  }, [order?.id]);

  useEffect(() => {
    requestIdRef.current += 1;
    setPreviousBids([]);
    setBidsLoaded(!shouldLoadTenderBids);

    if (order?.id && shouldLoadTenderBids) loadPreviousBids();

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadPreviousBids, order?.id, shouldLoadTenderBids]);

  const handleBidUpdate = async () => {
    setBidsLoaded(false);
    await loadPreviousBids();
    onForceUpdate?.();
  };

  if (!order) return null;

  const hasPreviousBid = previousBids.length > 0;

  if (isCanceled) {
    if (!bidsLoaded) return <div className={style.wrapper} />;

    if (hasPreviousBid) {
      return (
        <div className={style.wrapper}>
          <ExpeditorBidBlock
            onRefreshOrder={fetchOrderData}
            bid={previousBids[0]}
            order={order}
            isCanceled={isCanceled}
            canceledText={canceledText}
            isOtherExpeditorAssigned={isOtherExpeditorAssigned}
            onUpdateData={handleBidUpdate}
            onFindNewOrders={onClose}
          />
        </div>
      );
    }

    return (
      <div className={style.wrapper}>
        <CanceledOrderCard
          canceledStatus={canceledStatus}
          emptyReasonText="РЅРµ СѓРєР°Р·Р°РЅР°"
          onFindNewOrders={onClose}
        />
      </div>
    );
  }

  const showBidForm =
    status === "awaitingoffer" && !isTenderEnded && bidsLoaded && !hasPreviousBid;
  const showBidBlock = bidsLoaded && hasPreviousBid && shouldLoadTenderBids;
  const showTenderClosedInfo =
    bidsLoaded &&
    !hasPreviousBid &&
    ((status === "awaitingoffer" && isTenderEnded) || isTenderResultForOtherExpeditor);

  return (
    <div className={style.wrapper}>
      {showBidForm ? (
        <ExpeditorBidForm onRefreshOrder={fetchOrderData} order={order} onUpdateData={handleBidUpdate} />
      ) : null}

      {showBidBlock ? (
        <ExpeditorBidBlock
          onRefreshOrder={fetchOrderData}
          bid={previousBids[0]}
          order={order}
          isOtherExpeditorAssigned={isOtherExpeditorAssigned}
          onUpdateData={handleBidUpdate}
          onFindNewOrders={onClose}
        />
      ) : null}

      {showTenderClosedInfo ? <ExpeditorTenderClosedInfo /> : null}

      {ACTION_STATUSES.has(status) && !isOtherExpeditorAssigned ? (
        <ExpeditorStatusAction
          order={order}
          fetchOrderData={fetchOrderData}
          onForceUpdate={onForceUpdate}
        />
      ) : null}

      {status === "return" && !isOtherExpeditorAssigned ? (
        <ExpeditorReturnAction
          order={order}
          fetchOrderData={fetchOrderData}
          onForceUpdate={onForceUpdate}
        />
      ) : null}

      {!isOtherExpeditorAssigned ? (
        <>
          <ExpeditorExpectedArrivalBlock
            order={order}
            fetchOrderData={fetchOrderData}
          />

          <ExpeditorDocumentUpload
            order={order}
            refreshDocuments={refreshDocuments}
          />
        </>
      ) : null}

      <ExpeditorFinalStatusInfo order={order} />
    </div>
  );
};

export default ExpeditorActionsTab;
